import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Palette, Image as ImageIcon, Upload, Check, RotateCcw } from "lucide-react";
import { getBrandingCompleto, atualizarBranding, uploadBrandingFile } from "../services/branding";
import { anexoUrlAbsoluto } from "../services/tarefas";
import { aplicarBranding } from "../hooks/useBranding";
import { useAuth } from "../hooks/useAuth";
import { useConfirm } from "../components/ConfirmProvider";

export const NOME_PADRAO = "RECDC OS";
export const COR_PRIMARIA_PADRAO = "#10b981";   // verde — destaques de marca
export const COR_SECUNDARIA_PADRAO = "#2563eb"; // blue-600 — ação dominante
export const COR_CHAT_PADRAO = "#0ea5e9";        // sky-500 — exclusiva do chat
// Alias retrocompatível pro nome antigo.
export const COR_PADRAO = COR_SECUNDARIA_PADRAO;

// Pré-sets de cores rápidas
const PRESETS = [
  { cor: "#2563eb", label: "Azul" },
  { cor: "#0f172a", label: "Slate escuro" },
  { cor: "#10b981", label: "Verde" },
  { cor: "#7c3aed", label: "Roxo" },
  { cor: "#f59e0b", label: "Âmbar" },
  { cor: "#ef4444", label: "Vermelho" },
];

export function Branding() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: ["branding-completo"], queryFn: getBrandingCompleto, enabled: can(["admin"]) });

  const [nome, setNome] = useState("");
  const [cor, setCor] = useState(COR_PRIMARIA_PADRAO);
  const [corSecundaria, setCorSecundaria] = useState(COR_SECUNDARIA_PADRAO);
  const [corChat, setCorChat] = useState(COR_CHAT_PADRAO);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoEscala, setLogoEscala] = useState(48);
  const [faviconUrl, setFaviconUrl] = useState("");
  // MELHORIA 21 — logo (preta) do cabeçalho do relatório em PDF.
  const [logoRelatorioUrl, setLogoRelatorioUrl] = useState("");
  const [salvo, setSalvo] = useState(false);

  const logoRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);
  const logoRelRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data) {
      setNome(data.nome || NOME_PADRAO);
      setCor(data.corPrimaria || COR_PRIMARIA_PADRAO);
      setCorSecundaria(data.corSecundaria || COR_SECUNDARIA_PADRAO);
      setCorChat(data.corChat || COR_CHAT_PADRAO);
      setLogoUrl(data.logoUrl || "");
      setLogoEscala(parseInt(data.logoEscala || "48"));
      setFaviconUrl(data.faviconUrl || "");
      setLogoRelatorioUrl(data.logoRelatorioUrl || "");
    }
  }, [data]);

  const salvarM = useMutation({
    mutationFn: () => atualizarBranding({
      nome: nome || null,
      corPrimaria: cor || null,
      corSecundaria: corSecundaria || null,
      corChat: corChat || null,
      logoUrl: logoUrl || null,
      faviconUrl: faviconUrl || null,
      logoEscala: String(logoEscala),
      logoRelatorioUrl: logoRelatorioUrl || null,
    } as any),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ["branding"] });
      qc.invalidateQueries({ queryKey: ["branding-completo"] });
      aplicarBranding(b);
      try { window.dispatchEvent(new CustomEvent("growsoft:branding-atualizado")); } catch {}
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2000);
    },
  });

  const uploadLogoM = useMutation({
    mutationFn: (f: File) => uploadBrandingFile("logoUrl", f),
    onSuccess: (r) => { setLogoUrl(r.url); qc.invalidateQueries({ queryKey: ["branding-completo"] }); },
  });
  const uploadFaviconM = useMutation({
    mutationFn: (f: File) => uploadBrandingFile("faviconUrl", f),
    onSuccess: (r) => { setFaviconUrl(r.url); qc.invalidateQueries({ queryKey: ["branding-completo"] }); },
  });
  const uploadLogoRelM = useMutation({
    mutationFn: (f: File) => uploadBrandingFile("logoRelatorioUrl", f),
    onSuccess: (r) => { setLogoRelatorioUrl(r.url); qc.invalidateQueries({ queryKey: ["branding-completo"] }); },
  });

  const restaurarM = useMutation({
    mutationFn: () => atualizarBranding({
      nome: NOME_PADRAO,
      corPrimaria: COR_PRIMARIA_PADRAO,
      corSecundaria: COR_SECUNDARIA_PADRAO,
      corChat: COR_CHAT_PADRAO,
      logoUrl: null,
      faviconUrl: null,
      logoEscala: "48",
    } as any),
    onSuccess: (b) => {
      setNome(NOME_PADRAO);
      setCor(COR_PRIMARIA_PADRAO);
      setCorSecundaria(COR_SECUNDARIA_PADRAO);
      setCorChat(COR_CHAT_PADRAO);
      setLogoUrl("");
      setLogoEscala(48);
      setFaviconUrl("");
      qc.invalidateQueries({ queryKey: ["branding"] });
      qc.invalidateQueries({ queryKey: ["branding-completo"] });
      aplicarBranding({ ...b, nome: NOME_PADRAO, corPrimaria: COR_PRIMARIA_PADRAO, corSecundaria: COR_SECUNDARIA_PADRAO, corChat: COR_CHAT_PADRAO, logoUrl: undefined, faviconUrl: undefined });
      try { window.dispatchEvent(new CustomEvent("growsoft:branding-atualizado")); } catch {}
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2000);
    },
  });

  if (!can(["admin"])) return <div className="text-slate-500">Acesso restrito a administradores.</div>;
  if (isLoading) return <p className="text-sm text-slate-400">Carregando…</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Palette size={20} className="text-blue-600" /> Personalização da plataforma
        </h1>
        <p className="text-sm text-slate-500 mt-1">Aplica em todos os usuários (login, header, tabs do navegador).</p>
      </div>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div>
          <label className="text-xs text-slate-600">Nome da empresa / sistema</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={NOME_PADRAO}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
          />
        </div>

        <div>
          <label className="text-xs text-slate-600">Cor primária <span className="text-slate-400">(destaques de marca)</span></label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              className="w-14 h-10 rounded border border-slate-300 cursor-pointer"
            />
            <input
              type="text"
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm font-mono focus:border-blue-500"
              placeholder={COR_PRIMARIA_PADRAO}
            />
            <div className="w-20 h-10 rounded" style={{ background: cor }} />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESETS.map((p) => (
              <button
                key={"prim-" + p.cor}
                type="button"
                onClick={() => setCor(p.cor)}
                className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border transition-colors ${cor.toLowerCase() === p.cor.toLowerCase() ? "border-slate-700 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                title={p.label}
              >
                <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: p.cor }} />
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-600">
            Cor secundária <span className="text-slate-400">(ação dominante — botões, links, focus)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={corSecundaria}
              onChange={(e) => setCorSecundaria(e.target.value)}
              className="w-14 h-10 rounded border border-slate-300 cursor-pointer"
            />
            <input
              type="text"
              value={corSecundaria}
              onChange={(e) => setCorSecundaria(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm font-mono focus:border-blue-500"
              placeholder={COR_SECUNDARIA_PADRAO}
            />
            <div className="w-20 h-10 rounded" style={{ background: corSecundaria }} />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESETS.map((p) => (
              <button
                key={"sec-" + p.cor}
                type="button"
                onClick={() => setCorSecundaria(p.cor)}
                className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border transition-colors ${corSecundaria.toLowerCase() === p.cor.toLowerCase() ? "border-slate-700 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                title={p.label}
              >
                <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: p.cor }} />
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Substitui o azul padrão em botões, links, badges e focos no app inteiro.
            Tons claros e escuros são derivados automaticamente.
          </p>
        </div>

        <div>
          <label className="text-xs text-slate-600">
            Cor do chat <span className="text-slate-400">(bolhas, avatares de grupo, FAB, IA)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={corChat}
              onChange={(e) => setCorChat(e.target.value)}
              className="w-14 h-10 rounded border border-slate-300 cursor-pointer"
            />
            <input
              type="text"
              value={corChat}
              onChange={(e) => setCorChat(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm font-mono focus:border-blue-500"
              placeholder={COR_CHAT_PADRAO}
            />
            <div className="w-20 h-10 rounded" style={{ background: corChat }} />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESETS.map((p) => (
              <button
                key={"chat-" + p.cor}
                type="button"
                onClick={() => setCorChat(p.cor)}
                className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border transition-colors ${corChat.toLowerCase() === p.cor.toLowerCase() ? "border-slate-700 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                title={p.label}
              >
                <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: p.cor }} />
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Usada apenas dentro do chat (mensagens, ChatFloating, IA) — não afeta o resto da UI.
            Bom pra manter a cor secundária forte fora do chat sem invadir a leitura das conversas.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <BrandingArquivo
            label="Logo"
            url={logoUrl}
            onClear={() => setLogoUrl("")}
            onClick={() => logoRef.current?.click()}
            inputRef={logoRef}
            uploading={uploadLogoM.isPending}
            onSelect={(f) => uploadLogoM.mutate(f)}
            preview={
              logoUrl ? <img src={anexoUrlAbsoluto(logoUrl)} alt="logo" className="max-h-16 mx-auto" /> : null
            }
          />
          <BrandingArquivo
            label="Favicon (quadrado, ideal SVG)"
            url={faviconUrl}
            onClear={() => setFaviconUrl("")}
            onClick={() => faviconRef.current?.click()}
            inputRef={faviconRef}
            uploading={uploadFaviconM.isPending}
            onSelect={(f) => uploadFaviconM.mutate(f)}
            preview={
              <img
                src={faviconUrl ? anexoUrlAbsoluto(faviconUrl) : "/favicon.svg"}
                alt="favicon"
                className="w-10 h-10 mx-auto"
              />
            }
            hint={!faviconUrl ? "Padrão do sistema (deixe vazio para usar)" : undefined}
          />
          {/* MELHORIA 21 — logo do relatório (PDF). Deve ser PRETA/escura: o PDF sai
              em fundo branco e a logo branca da sidebar fica invisível. */}
          <BrandingArquivo
            label="Logo do relatório (PDF)"
            url={logoRelatorioUrl}
            onClear={() => setLogoRelatorioUrl("")}
            onClick={() => logoRelRef.current?.click()}
            inputRef={logoRelRef}
            uploading={uploadLogoRelM.isPending}
            onSelect={(f) => uploadLogoRelM.mutate(f)}
            preview={
              logoRelatorioUrl ? <img src={anexoUrlAbsoluto(logoRelatorioUrl)} alt="logo relatório" className="max-h-16 mx-auto" /> : null
            }
            hint={logoRelatorioUrl ? undefined : "Use a versão PRETA (fundo branco no PDF). Vazio = logo padrão recdc."}
          />
        </div>

        {/* Tamanho da logo + preview ao vivo */}
        <div className="border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-slate-600">Tamanho da logo na sidebar</label>
            <span className="text-[11px] font-mono text-slate-500">{logoEscala}px</span>
          </div>
          <input
            type="range"
            min={20}
            max={80}
            step={1}
            value={logoEscala}
            onChange={(e) => setLogoEscala(parseInt(e.target.value))}
            disabled={!logoUrl}
            className="w-full accent-blue-600 disabled:opacity-50"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
            <span>20px (compacta)</span>
            <button
              type="button"
              onClick={() => setLogoEscala(48)}
              className="hover:text-slate-600 underline"
            >
              48 (padrão)
            </button>
            <span>80px (grande)</span>
          </div>
          {!logoUrl && <p className="text-[11px] text-slate-400 mt-1">Suba uma logo acima para habilitar o ajuste.</p>}

          {/* Preview ao vivo da sidebar */}
          <div className="mt-4">
            <p className="text-xs text-slate-600 mb-2">Pré-visualização (sidebar)</p>
            <PreviewSidebar nome={nome || NOME_PADRAO} logoUrl={logoUrl} logoEscala={logoEscala} cor={cor} />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={async () => {
              const ok = await confirm({
                title: "Restaurar padrão",
                message: `Voltar para "${NOME_PADRAO}" + cor azul padrão e remover logo/favicon. Esta ação salva imediatamente.`,
                confirmText: "Restaurar",
              });
              if (ok) restaurarM.mutate();
            }}
            disabled={restaurarM.isPending}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50"
            title={`Restaurar para "${NOME_PADRAO}" e cor azul padrão`}
          >
            <RotateCcw size={14} /> {restaurarM.isPending ? "Restaurando…" : "Restaurar padrão"}
          </button>
          <button
            onClick={() => salvarM.mutate()}
            disabled={salvarM.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {salvo ? <><Check size={14} /> Salvo</> : (<><Save size={14} /> {salvarM.isPending ? "Salvando…" : "Salvar"}</>)}
          </button>
        </div>
      </section>
    </div>
  );
}

function BrandingArquivo({ label, url, onClear, onClick, inputRef, uploading, onSelect, preview, hint }: {
  label: string;
  url: string;
  onClear: () => void;
  onClick: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  uploading: boolean;
  onSelect: (f: File) => void;
  preview: React.ReactNode;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <div className="border border-dashed border-slate-300 rounded-lg p-3 text-center bg-slate-50">
        <div className="min-h-[64px] flex items-center justify-center mb-2">
          {preview || <ImageIcon size={28} className="text-slate-300" />}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={onClick}
            disabled={uploading}
            className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
          >
            <Upload size={12} /> {uploading ? "Enviando…" : "Enviar"}
          </button>
          {url && (
            <button onClick={onClear} className="text-xs text-slate-500 hover:text-red-500 underline">Remover</button>
          )}
        </div>
        {hint && <p className="text-[10px] text-slate-400 mt-2">{hint}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSelect(f);
            if (inputRef.current) inputRef.current.value = "";
          }}
        />
      </div>
    </div>
  );
}

/**
 * Mini-preview da sidebar real, refletindo as escolhas atuais (logo, escala, cor, nome)
 * sem precisar salvar.
 */
function PreviewSidebar({ nome, logoUrl, logoEscala, cor }: { nome: string; logoUrl: string; logoEscala: number; cor: string }) {
  const URL_PREVIEW = logoUrl ? anexoUrlAbsoluto(logoUrl) : null;
  return (
    <div className="rounded-lg overflow-hidden border border-slate-300 shadow-sm flex" style={{ height: 220 }}>
      <aside className="w-44 bg-slate-900 text-white flex flex-col">
        <div className="p-3 border-b border-slate-800 flex items-center justify-center overflow-hidden" style={{ minHeight: 64 }}>
          {URL_PREVIEW ? (
            <div
              className="flex items-center justify-center max-w-full"
              style={{ height: `${logoEscala}px` }}
            >
              <img
                src={URL_PREVIEW}
                alt="logo preview"
                className="h-full w-auto max-w-full object-contain"
              />
            </div>
          ) : (
            <div>
              <p className="text-sm font-bold tracking-tight">{nome}</p>
              <p className="text-[10px] text-slate-400">Project Manager</p>
            </div>
          )}
        </div>
        <div className="p-2 space-y-1 flex-1">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-white text-xs font-medium" style={{ background: cor }}>
            <span className="w-3 h-3 rounded-full bg-white/30" /> Dashboard
          </div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-slate-200 text-xs">
            <span className="w-3 h-3 rounded-full bg-slate-600" /> Projetos
          </div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-slate-200 text-xs">
            <span className="w-3 h-3 rounded-full bg-slate-600" /> Tarefas
          </div>
        </div>
      </aside>
      <div className="flex-1 bg-slate-50 p-3 text-[10px] text-slate-400">
        <div className="bg-white border border-slate-200 rounded px-2 py-1 mb-2 inline-flex items-center gap-1">
          <span className="font-semibold text-slate-700">Dashboard</span>
        </div>
        <div className="bg-white border border-slate-200 rounded h-16 mb-2" />
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white border border-slate-200 rounded h-12" />
          <div className="bg-white border border-slate-200 rounded h-12" />
        </div>
      </div>
    </div>
  );
}
